import { useCallback } from 'react';
import { useHumanInTheLoop } from '@copilotkit/react-core';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetLoggedUser } from '@/hooks/useGetLoggedUser';
import { CopilotStatus, TOOL_NAMES } from '@/helpers/aiChat/tools';
import { SupportTicketFormRenderer } from './SupportTicketFormRenderer';

/**
 * Registers CopilotKit tool for opening a support center ticket form inline in chat.
 * The AI gathers issue details through conversation, then calls this tool to render
 * a pre-filled form the user can review and submit.
 *
 * @example
 * function ChatPage() {
 *   useOpenSupportTicketTool();
 *   return <CopilotChat />;
 * }
 *
 * @param options.enabled - When false, the tool stays registered (stable hook
 * call order) but is hidden from the LLM via CopilotKit's `available: 'disabled'`.
 * // TODO(TF-677): remove once gigawatt_setup_assistant entitlement ships and this
 * // is no longer needed to scope setup-assistant-only orgs' tool access.
 */
export const useOpenSupportTicketTool = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const organizationId = useGetOrganizationId() ?? '';
  const loggedUser = useGetLoggedUser();
  const userId = loggedUser?.id ?? '';
  const employeeId = loggedUser?.employeeId ?? '';

  const renderSupportTicketTool = useCallback(
    (props: {
      args: Record<string, unknown>;
      status: CopilotStatus;
      respond?: (result: string) => void;
    }) => (
      <SupportTicketFormRenderer
        {...props}
        organizationId={organizationId}
        userId={userId}
        employeeId={employeeId}
      />
    ),
    [organizationId, userId, employeeId]
  );

  useHumanInTheLoop({
    name: TOOL_NAMES.OPEN_SUPPORT_TICKET,
    description: `Opens a support ticket form in chat. When suggesting this to the user, say something like "I'd recommend submitting a support ticket" — do not mention Electric or imply the ticket goes to Electric's team; tickets are routed internally to the customer's own support champion for that category.

Use a three-step calling pattern:

CALL 1 — Provide only \`category\` ("Applications", "Device", "Connectivity", or "Something Else").
You will receive the available request types and the logged-in employee info.
Present the request types to the user and ask which one fits their issue.

CALL 2 — Provide \`category\` + \`request_type_id\` (the ID the user chose).
You will receive the field definitions (name, displayName, type, options, required, prompt, parentRequestTypeSettingId, parentSettingValue).
Before asking any questions, infer as many field values as you can from what the user has already said in the conversation. Only ask about fields you genuinely cannot infer. When you do ask, group remaining unknowns into a single message rather than one question per field.

CALL 3 — Provide \`category\` + \`request_type_id\` + \`field_values\` (object mapping each field name to the value gathered from conversation).
This renders the pre-filled form for the user to review and submit.

REQUIRED FIELDS CHECKLIST — complete this before making Call 3:
1. Every field with required=true must have a value in field_values.
2. Conditional fields (those with a parentRequestTypeSettingId) become visible — and required — when their parent field's value equals parentSettingValue. If the parent field's value you are about to submit would trigger a conditional required field, you MUST gather that child field's value before making Call 3. Do not assume the user will fill it in on the form.
3. When in doubt whether a conditional field will be triggered, ask — a single extra question is better than rendering a form the user cannot submit.

Prefer a confident assumption over an unnecessary question, but never submit Call 3 with a required field (direct or conditional) missing.

For employeeDropdown fields, pass the full employee object from get_organization_employees or get_employee: { employee_id, first_name, last_name, email }. For the logged-in user use loggedEmployee values.

For applicationDropdown fields, pass the application name as a string (e.g. "Slack"). If the application is not integrated with Electric, the form will automatically fall back to "Application not listed" — always pass the name the user provided and let the form handle the rest.`,
    parameters: [
      {
        name: 'category',
        type: 'string',
        description:
          'The support ticket category: "Applications", "Device", "Connectivity", or "Something Else"',
        required: true,
      },
      {
        name: 'request_type_id',
        type: 'string',
        description:
          'The ID of the specific request type chosen by the user. Omit on Call 1.',
        required: false,
      },
      {
        name: 'field_values',
        type: 'object',
        description:
          'Object mapping each field name to the value gathered from conversation. Only include on Call 3. Must contain values for every required field, including conditional fields whose parent value would make them visible and required.',
        required: false,
      },
    ],
    render: renderSupportTicketTool,
    available: enabled ? 'enabled' : 'disabled',
  });
};
